/** Pure trend/aggregation maths for the shared KPI grid, extracted verbatim from dashboard-new. */

import { formatDate } from '@angular/common';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface DailyPoint {
  date: string;
  value: number;
}

/**
 * A date paired with the two sides of a rate, so a period's rate can be recomputed from summed
 * totals rather than by averaging daily rates (which would weight quiet days equally).
 */
export type RatioEntry = [string, { identified: number; total: number }];

/** A named series in a graph response (`open`, `conversion`, `CreatedSubscribers`, ...). */
export interface NamedGroup {
  name: string;
  count?: number;
  items: { date: string; count: number }[];
}

/**
 * Percentage change from `prev` to `curr`. With no previous value there is no meaningful
 * percentage, so any growth from zero reports as +100% rather than infinity.
 */
export function percentChange(prev: number, curr: number): number {
  if (prev > 0) {
    return ((curr - prev) / prev) * 100;
  }
  return curr > 0 ? 100 : 0;
}

export function sumPoints(points: { value: number }[]): number {
  return points.reduce((sum, point) => sum + point.value, 0);
}

/** Ratio of summed numerators to summed denominators, as a percentage. */
export function ratioOfTotals(entries: RatioEntry[]): number {
  let identified = 0;
  let total = 0;
  entries.forEach(([, value]) => {
    identified += value.identified;
    total += value.total;
  });
  return total > 0 ? (identified / total) * 100 : 0;
}

/**
 * An odd count drops the middle day, so both halves span the same number of days; a summed metric
 * would otherwise read the extra day as growth. `avg` suits rates and levels, `sum` suits counts.
 */
export function computeHalfSplitChange(points: DailyPoint[], mode: 'sum' | 'avg'): number | null {
  const sorted = [...points].sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length < 2) {
    return null;
  }
  const mid = Math.floor(sorted.length / 2);
  const firstHalf = sorted.slice(0, mid);
  const secondHalf = sorted.slice(sorted.length - mid);
  const aggregate = (slice: DailyPoint[]): number => {
    const total = slice.reduce((sum, point) => sum + point.value, 0);
    return mode === 'avg' ? total / slice.length : total;
  };
  return percentChange(aggregate(firstHalf), aggregate(secondHalf));
}

export function ratioOfTotalsHalfSplitChange(sortedEntries: RatioEntry[]): number | null {
  if (sortedEntries.length < 2) {
    return null;
  }
  const mid = Math.floor(sortedEntries.length / 2);
  return percentChange(
    ratioOfTotals(sortedEntries.slice(0, mid)),
    ratioOfTotals(sortedEntries.slice(sortedEntries.length - mid)),
  );
}

/** The extra day is what makes the window two whole periods rather than one day short of it. */
export function previousPeriodStart(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const durationMs = end.getTime() - start.getTime();
  return new Date(start.getTime() - durationMs - MS_PER_DAY).toISOString();
}

/** Feb 29 rolls over to Mar 1, matching Date's own behaviour. */
export function minusOneYear(date: string): string {
  const shifted = new Date(date);
  shifted.setFullYear(shifted.getFullYear() - 1);
  return shifted.toISOString();
}

export function localDay(date: Date): string {
  return formatDate(date, 'yyyy-MM-dd', 'en-US');
}

/** Whole UTC days on the local dates, since east of UTC a local midnight is still the day before in UTC. */
export function utcDayRange(start: Date, end: Date): { startDate: string; endDate: string } {
  return { startDate: `${localDay(start)}T00:00:00.000Z`, endDate: `${localDay(end)}T23:59:59.999Z` };
}

/** A missing series yields no points rather than throwing. */
export function extractDaily(items: NamedGroup[], name: string): DailyPoint[] {
  return (items.find((item) => item.name === name)?.items ?? []).map((item) => ({
    date: item.date,
    value: item.count ?? 0,
  }));
}

/**
 * Daily points for two series summed per date, for metrics the backend splits into a full and a
 * partial variant (`conversion` + `conversionPartial`).
 */
export function extractDailyMerged(items: NamedGroup[], first: string, second: string): DailyPoint[] {
  const byDate = new Map<string, number>();
  [
    ...(items.find((item) => item.name === first)?.items ?? []),
    ...(items.find((item) => item.name === second)?.items ?? []),
  ].forEach((item) => byDate.set(item.date, (byDate.get(item.date) ?? 0) + (item.count ?? 0)));
  return toSortedPoints(byDate);
}

export function extractRatioEntries(
  items: NamedGroup[],
  numeratorNames: string[],
  denominatorName: string,
): RatioEntry[] {
  const denominators = new Map<string, number>(
    (items.find((item) => item.name === denominatorName)?.items ?? []).map((item) => [item.date, item.count ?? 0]),
  );
  const numerators = new Map<string, number>();
  numeratorNames.forEach((name) => {
    (items.find((item) => item.name === name)?.items ?? []).forEach((item) => {
      numerators.set(item.date, (numerators.get(item.date) ?? 0) + (item.count ?? 0));
    });
  });
  return Array.from(denominators.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, total]): RatioEntry => [date, { identified: numerators.get(date) ?? 0, total }]);
}

export function extractDailyRate(items: NamedGroup[], numeratorNames: string[], denominatorName: string): DailyPoint[] {
  return extractRatioEntries(items, numeratorNames, denominatorName).map(([date, { identified, total }]) => ({
    date,
    value: total > 0 ? (identified / total) * 100 : 0,
  }));
}

function toSortedPoints(byDate: Map<string, number>): DailyPoint[] {
  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, value]) => ({ date, value }));
}
